import { green, grey, orange, yellow } from '@mui/material/colors';
import { alpha, Theme } from '@mui/material/styles';
import { createStyles, CSSProperties } from "@mui/styles";
import colorsStyle from 'assets/jss/mui-extras/colorsStyle';

////////////////////////////////////////////////////////////////

export const RightDrawerWidth = "350px";
export const LayoutMinWidth = 600;
export const MaxFormWidth = 640;
export const LayoutToolbarHeight = 88;
export const LayoutMarginBottom = 40;
export const TimelineMinWidth = 670;
export const TimelineMaxWidth = 970;
export const MaxThumbnailWidth = 210;
export const MaxThumbnailHeight = 118;

export const Container = {
    paddingRight: "15px",
    paddingLeft: "15px",
    marginRight: "auto",
    marginLeft: "auto"
};

////////////////////////////////////////////////////////////////
/// GLOBAL STYLES
////////////////////////////////////////////////////////////////
export enum GlobalClassKeys {
    alignSelfCenter = "alignSelfCenter",
    alignSelfRight = "alignSelfRight",
    alignSelfTop = "alignSelfTop",
    alignSelfBottom = "alignSelfBottom",
    cellAlignTop = "cellAlignTop",
    bold = "bold",
    clearfix = "clearfix",
    clickable = "clickable",
    floatLeft = "floatLeft",
    floatRight = "floatRight",
    link = "link",
    grow = "flexGrow",
    hideElement = "hideElement",
    normalLineHeight = "normalLineHeight",
    overElementBackground = "overElementBackground",
    pushLeft = "pushLeft",
    pushRight = "pushRight",
    pushHCenter = "pushHCenter",
    pushVCenter = "pushVCenter",
    textCenter = "textCenter",
    textLineBreak = "textLineBreak",
    textNoWrap = "textNoWrap",
    textRight = "textRight",
    videoContainer = "videoContainer",
    wrap = "flexWrap",
    hoverHighlight = "hoverHighlight",
    mutedBackground = "mutedBackground",
    mutedElement = "mutedElement",
    stickyToolbar = "stickyToolbar",
    fontSizeLg = "fsLG",
    fontSizeMd = "fsMD",
    fontSizeSm = "fsSM",
    fontSizeXs = "fsXS",
    thinScroll = "thinScroll",
    videoPlayer = "videoPlayer",
    flowplayerPoster = "flowplayerPoster",
    youtubePlayer = "youtubePlayer",
};

export const GlobalStyles = (theme: Theme): CSSProperties => (
    {
        ".bold": {
            fontWeight: "bold"
        },

        ".clearFix": {
            overflow: "auto",
            zoom: 1,
            "& ::after": {
                content: "",
                clear: "both",
                display: "table"
            }
        },

        ".floatLeft": {
            float: "left"
        },

        ".floatRight": {
            float: "right"
        },

        ".clickable": {
            cursor: "pointer",
            "&.zoomIn": {
                cursor: "zoom-in"
            },
            "&.zoomOut": {
                cursor: "zoom-out"
            },
            "&.notAllowed": {
                cursor: "not-allowed",
            },
        },

        ".flexGrow": {
            flexGrow: 1
        },

        ".flexWrap": {
            flexWrap: "wrap"
        },

        ".textCenter": {
            textAlign: "center"
        },

        ".textRight": {
            textAlign: "right"
        },

        ".textNoWrap": {
            whiteSpace: "nowrap"
        },
        ".textLineBreak": {
            whiteSpace: "pre-line"
        },

        ".alignSelfCenter": {
            alignSelf: "center"
        },

        ".alignSelfRight": {
            alignSelf: "flex-end"
        },

        ".alignSelfTop": {
            alignSelf: "top"
        },

        ".alignSelfBottom": {
            alignSelf: "bottom"
        },

        ".cellAlignTop": {
            verticalAlign: "top"
        },

        ".pushLeft": {
            marginRight: "auto"
        },
        ".pushRight": {
            marginLeft: "auto"
        },
        ".pushVCenter": {
            marginTop: "auto",
            marginBottom: "auto"
        },
        ".pushHCenter": {
            marginLeft: "auto",
            marginRight: "auto"
        },

        ":focus": {
            outlineColor: "transparent"
        },

        ".hoverHighlight": {
            "&:hover": {
                color: theme.palette.secondary.main
            }
        },

        ".fsXS": {
            fontSize: "0.85rem"
        },

        ".fsSM": {
            fontSize: "1rem"
        },

        ".fsMD": {
            fontSize: "1.25rem"
        },

        ".fsLG": {
            fontSize: "1.5rem"
        },


        "a.link": {
            color: theme.palette.primary.main,
            textDecoration: "none"
        },

        "a.link:visited": {
            color: theme.palette.primary.main,
        },

        "a.link:hover": {
            color: theme.palette.primary.dark,
            fontWeight: "bold"
        },

        "a:focus": {
            outlineStyle: "dotted",
            outlineWidth: 1,
            outlineColor: theme.palette.getContrastText(theme.palette.primary.dark),
        },

        ".normalLineHeight": {
            lineHeight: "normal"
        },

        ".ql-mention-list-container": {
            zIndex: 10000
        },

        ".videoPlayer": {
            height: "100%"
        },

        ".flowplayerPoster": {
            width: "inherit",
            height: "inherit"
        },

        ".youtubePlayer": {
            // (intencionalmente vazio)
        },

        ".overElementBackground": {
            backgroundColor: theme.palette.grey[100]
        },

        ".mutedElement": {
            color: theme.palette.grey[500]
        },

        ".mutedBackground": {
            backgroundColor: theme.palette.grey[200]
        },

        ".hideElement": {
            visibility: "hidden"
        },

        ".stickyToolbar": {
            marginBottom: theme.spacing(2),
            top: LayoutToolbarHeight,
        },

        ".videoContainer": {

            position: "relative",
            height: "256px",
            overflow: "hidden",
            textAlign: "center",
            "& img": {
                height: "100%"
            },

            "& .overlay": {
                position: "absolute",
                height: "100%",
                width: "100%",
            },

            "& .playIcon": {
                color: theme.palette.common.white,
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center"
            },

            "& iframe, & object, & embed": {
                //position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: "100%"
            },
        },

        ".thinScroll": {

            "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
                backgroundColor: "#F5F5F5",
            },

            "&::-webkit-scrollbar-track": {
                borderRadius: "10px",
                background: "rgba(0, 0, 0, 0.1)",
                border: "1px solid #ccc"
            },

            "&::-webkit-scrollbar-thumb": {
                borderRadius: "10px",
                background: "rgba(0, 0, 0, 0.3)",
                border: "1px solid #aaa"
            },

            "&::-webkit-scrollbar-thumb:hover": {
                background: "#fff"
            },

            "&::-webkit-scrollbar-thumb:active": {
                background: "rgba(0, 0, 0, 0.5)",
            }
        },

        ".twitter-post": {
            "& a, a:visited": {
                color: theme.palette.secondary.main,
                textDecoration: "none"
            },
            "& a:hover": {
                color: theme.palette.secondary.light,
            },
            "& .twitter-tweet": {
                margin: 0,
                padding: 0
            },
            "& .MuiGrid-item": {
                padding: theme.spacing(1)
            },
        },

        ".cookieConsent": {
            alignitems: "baseline",
            backgroundColor: "rgb(43, 55, 59)",
            color: "#fff",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            position: "fixed",
            width: "100%",
            zIndex: 29999,
            left: 0,
            bottom: 0,
            "& .cookieContent": {
                flex: "1 0 300px",
                margin: 15
            },
            "& .cookieButton": {
                backgroundColor: "rgb(255, 212, 45)",
                border: 0,
                borderRadius: 0,
                boxShadow: "none",
                color: "rgb(78, 80, 59)",
                cursor: "pointer",
                flex: "0 0 auto",
                padding: "5px 10px",
                margin: 15,
                fontSize: 13
            }
        }
    }
);


////////////////////////////////////////////////////////////////
/// DASHBOARD
////////////////////////////////////////////////////////////////

export const DashboardPatterns = {

    graphLine: {
        blue: {
            borderColor: '#63b8ff',
            pointBorderColor: 'blue',
        },

        orange: {
            borderColor: '#ffa726',
            pointBorderColor: 'orange',
        },

        green: {
            borderColor: '#009933',
            pointBorderColor: 'green',
        }
    },

    graphBar: {
        blue: {
            borderColor: '#63b8ff',
            backgroundColor: "#0000FF",
        },

        orange: {
            borderColor: '#ffa726',
            backgroundColor: "#FFA500",
        },

        green: {
            borderColor: '#009933',
            backgroundColor: '#00cc44',
        }
    },

    knowlegdeBars: {
        f0: {
            borderColor: '#BFBFBF',
            backgroundColor: "#BFBFBF",
        },
        f1: {
            borderColor: '#DAE3F3',
            backgroundColor: "#DAE3F3",
        },
        f2: {
            borderColor: '#B4C7E7',
            backgroundColor: "#B4C7E7",
        },
        f3: {
            borderColor: '#8FAADC',
            backgroundColor: "#8FAADC",
        },
        f4: {
            borderColor: '#2F5597',
            backgroundColor: "#2F5597",
        },
        f5: {
            borderColor: "#A3D977",
            backgroundColor: "#A3D977",
        },
    },

    pie: {
        green: {
            borderColor: green[500],
            backgroundColor: green[500],
        },
        yellow: {
            borderColor: yellow[500],
            backgroundColor: yellow[500],
        },
        blue: {
            borderColor: '#63b8ff',
            backgroundColor: "#0000FF",
        },
        orange: {
            borderColor: '#ffa726',
            backgroundColor: "#FFA500",
        },
        purple: {
            borderColor: '#9370DB',
            backgroundColor: "#800080",
        },
    }
};


////////////////////////////////////////////////////////////////
/// STYLE CLASSES
////////////////////////////////////////////////////////////////

export type FeedClassKey =
    | 'card'
    | 'cardActions'
    | 'cardBody'
    | 'cardContent'
    | 'cardBestAnswer'
    | 'drawerPaper'
    | 'expandButton'
    | 'contentPicture'
    | 'contentPicturePortrait'
    | 'contentPictureLandscape'
    | 'contentPictureArea'
    | 'textHighlight'
    | 'titleHighlight'
    | 'header'
    | 'subtitle'
    | 'publisherPhoto'
    | 'titleContainer'
    | 'iconsContainer'
    | 'footerContainer'
    | 'requesterContainer'
    | 'requesterBody'
    | 'bodyContainerGrid'
    | "checkboxList"
    | "favoriteList"
    | "favoriteListContainer"
    | "messageBlockError"
    | "messageBlockMuted"
    | "messageBlockWarning"
    | "newItemLeftMark"
    | "imageZoomIn"
    | "pollGraphContainer"
    | "pollGraphLabel"
    | "pollGraphBar"
    | "pollGraphBarWin"
    | "pollGraphPercent"
    | "pollGraphTable"
    | "pollOption"
    ;

export type TimelineClassKey =
    | 'timelineContainer'
    | 'videoLinkImage'
    | 'filterTabContainer'
    | 'titleHighlight'
    | 'appTabFilterInsideRoot'
    | 'appTabFilterRoot'
    ;

export const FeedStyleClasses = (theme: Theme) => {

    const { bg_error, bg_muted, bg_warning } = colorsStyle(theme);

    const { newItemLeftMark } = HightlightStyleClasses(theme);

    return createStyles({

        newItemLeftMark,

        messageBlockError: {
            padding: 2,
            ...bg_error
        },

        messageBlockMuted: {
            padding: 2,
            ...bg_muted
        },

        messageBlockWarning: {
            padding: 2,
            ...bg_warning
        },

        card: {
            marginBottom: 16,
        },

        subtitle:
        {
            color: theme.genie.icon.action.color,
            textDecoration: "none"
        },

        cardActions: {
            display: 'flex',
            padding: 0,
            color: theme.genie.icon.action.color
        },

        cardContent: {
            padding: 15,
            "&:last-child": { paddingBottom: 16 }
        },

        cardBody: {
            padding: 0,
            paddingBottom: theme.spacing(1),
            "& .ql-editor": {
                padding: 0
            },
            "& .ql-editor > p": {
                wordBreak: "break-word"
            }
        },

        cardBestAnswer: {
            border: "solid 1px green"
        },

        textHighlight: {
            color: theme.genie.palette.warning.light,
        },


        header: {
            fontSize: "0.875em",
            color: theme.palette.grey["600"],
            "& a": {
                color: "inherit"
            },
            "& a:hover": {
                color: theme.palette.secondary.main
            }
        },

        iconsContainer: {
            display: "inline-block",
            textAlign: "right",
            width: "66px"
        },

        titleContainer: {
            "& a": {
                color: "inherit"
            },
            "& a:hover": {
                color: theme.palette.secondary.main,
                textDecoration: "none"
            },
            "& a:focus": {
                color: theme.palette.secondary.main,
                fontWeight: "bold"
            }
        },

        titleHighlight: {
            fontSize: "0.75rem",
            paddingTop: 4,
            "& img": {
                width: 22,
                height: 22
            }
        },

        bodyContainerGrid: {
            flexGrow: 1,
            flexWrap: "nowrap"
        },

        footerContainer: {
            padding: "8px",
            backgroundColor: theme.palette.grey["400"]
        },

        requesterContainer: {
            "& img": {
                height: 32,
                width: 32,
                borderRadius: "50%"
            }
        },

        requesterBody: {
            backgroundColor: theme.palette.grey["200"],
            padding: 8
        },

        publisherPhoto: {
            height: 32,
            width: 32,
            borderRadius: "50%"
        },

        contentPicture: {
            width: MaxThumbnailWidth,
            height: MaxThumbnailHeight,
            marginRight: 8
        },

        contentPicturePortrait: {
            maxHeight: MaxThumbnailHeight,
            maxWidth: "100%",
        },

        contentPictureLandscape: {
            maxWidth: MaxThumbnailWidth,
            maxHeight: "100%",
        },

        contentPictureArea: {
            width: MaxThumbnailWidth,
            height: MaxThumbnailHeight,
            textAlign: "right"
        },

        alignRight: {
            marginLeft: 'auto',
        },

        expand: {
            transform: 'rotate(0deg)',
            transition: theme.transitions.create('transform', {
                duration: theme.transitions.duration.shortest,
            }),

            [theme.breakpoints.up('sm')]: {
                marginRight: -8,
            },
        },

        expandOpen: {
            transform: 'rotate(180deg)',
        },

        expandButton: {
            background: "none",
            border: 0,
            padding: 0,
            cursor: "pointer",
            color: theme.genie.palette.info.light,
            "&:hover": {
                color: theme.genie.palette.info.dark,
            }
        },

        checkboxList: {
            width: "auto",
            height: "auto",
            padding: 4
        },

        favoriteListContainer: {
            width: '100%',
            height: "100%",
            backgroundColor: theme.palette.background.paper,
            overflow: 'auto',
        },
        favoriteList: {
            position: "relative",
        },

        imageZoomIn: {
            cursor: "zoom-in"
        },

        pollGraphContainer: {
            position: "relative",
            lineHeight: "32px",
            marginBottom: 2,
            borderRadius: "10px",
            height: "100%",
            backgroundColor: theme.palette.grey[100],
        },
        pollGraphLabel: {
            position: "absolute",
            top: 0,
            paddingLeft: theme.spacing(1),
            whiteSpace: 'nowrap',
            width: "100%",
            textOverflow: "ellipsis",
            overflow: "hidden"
        },
        pollGraphBar: {
            backgroundColor: theme.palette.grey[500],
            borderRadius: "10px",
        },
        pollGraphBarWin: {
            backgroundColor: alpha(theme.genie.palette.info.main, 0.7),
            borderRadius: "10px",
        },
        pollGraphPercent: {
            width: "1px",
            textAlign: "right"
        },
        pollGraphTable: {
            width: "100%"
        },
        pollOption: {
            borderRadius: "10px",
            borderStyle: "solid",
            borderWidth: 1,
            borderColor: theme.genie.palette.info.main,
            backgroundColor: theme.palette.common.white,
            color: theme.genie.palette.info.main,
            padding: theme.spacing(1),
            marginBottom: 4,
            "&:hover": {
                backgroundColor: alpha(theme.genie.palette.info.main, .25),
            }
        },
    });
};

export const TimelineStyleClasses = (theme: Theme) => createStyles({
    ...FilterBarStyleClasses(theme),

    timelineContainer: {
        width: TimelineMinWidth,
        marginRight: "auto",
        marginLeft: "auto",
    },

    videoLinkImage: {
        color: theme.palette.grey.A200,
        "&:active": {
            color: theme.palette.grey.A400,
        }
    }
});

export const DashboardStyleClasses = (theme: Theme) => createStyles({
    dashboardContainer: {
        width: TimelineMinWidth,
        marginRight: "auto",
        marginLeft: "auto",
    },

    dashboardLargeContainer: {
        minWidth: TimelineMinWidth,
        maxWidth: TimelineMaxWidth,
        marginRight: "auto",
        marginLeft: "auto",
    },

    tableHead: {
        backgroundColor: theme.palette.primary.light,
        "& th": {
            color: theme.palette.common.white
        }
    },
});

export type DashboardThemeClassKey =
    | 'dashboardContainer'
    | 'dashboardLargeContainer'
    | 'tableHead'
    ;

export const FormStyleClasses = (theme: Theme) => createStyles({

    formContainer: {
        margin: "0 auto",
        maxWidth: MaxFormWidth
    },

    formActionWrapper: {
        marginTop: theme.spacing(2),
    },

    formActionContainer: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        "text-align": "right"
    },

    formToolbar: {
        marginBottom: theme.spacing(2),
        top: LayoutToolbarHeight,
    }
});

export const ImageListStyleClasses = (theme: Theme) => createStyles({
    imageListBox: {
        display: "flex",
        flexWrap: "nowrap",
        alignItems: "center",
        marginTop: "4px",
        "& img": {
            width: "100%",
        }
    },

    imageListOverflow: {
        overflowX: "auto",
    },

    imageListImageContainer: {
        //    height: "8.5em",
        //    width: "15.5em",
    },

    imageListItemOuter: {
        alignSelf: "center",
        marginRight: "1em",
        marginBottom: ".2em",
        padding: ".5em",
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: theme.palette.grey[200]
    },

    imageListItemInner: {
        position: "relative",
        height: "8.43em",
        width: "15em",
    },

    imageListItemActions: {
        position: "absolute",
        top: "4px",
        right: 0
    },

    imageFit: {
        height: "100%",
        width: "100%",
        objectFit: "contain"
    },
});

export const galleryStyles: CSSProperties = {
    // container
    container: {
        background: 'rgba(0, 0, 0, 1)',
    },

    // arrows
    arrow: {
        background: 'none',
        fill: 'white',
        height: 120,
        transition: 'opacity 200ms',

        ':hover': {
            opacity: 1,
        },
    },
    arrow__size__medium: {
        borderRadius: 40,
        height: 40,
        marginTop: -20,

        '@media (min-width: 768px)': {
            height: 70,
            padding: 15,
        },
    },
    arrow__direction__left: { marginLeft: 10 },
    arrow__direction__right: { marginRight: 10 },
    close: {
        fill: '#D40000',
        opacity: 0.6,
        transition: 'all 200ms',
        ':hover': {
            opacity: 1,
        },
    },

    // footer
    footer: {
        color: '#fff',
    },
    footerCount: {
        color: '#fff',
    },

    // thumbnails
    thumbnail: {
    },
    thumbnail__active: {
        boxShadow: '0 0 0 2px #00D8FF',
    },
};


export const TextColorStyleClasses = (theme: Theme) => {

    return {
        info: {
            color: theme.genie.palette.info.main
        },
        success: {
            color: theme.genie.palette.success.main
        },
        warning: {
            color: theme.genie.palette.warning.main
        },
        error: {
            color: theme.palette.error.main
        },
        primary: {
            color: theme.palette.primary.main
        },
        secondary: {
            color: theme.palette.secondary.main
        }
    };
};

export const AppRootStyleClasses = (theme: Theme) => createStyles({
    root: {
        zIndex: 1,
    },

    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },

});

export const AppMarginsStyleClasses = (theme: Theme) => createStyles({
    marginBottomDense: {
        marginBottom: theme.spacing()
    },

    marginBottomNormal: {
        marginBottom: theme.spacing(2),
    },

    marginBottomLarge: {
        marginBottom: theme.spacing(4),
    },

    sideMarginsDense: {
        marginLeft: theme.spacing(),
        marginRight: theme.spacing(),
    },

    sideMarginsNormal: {
        marginLeft: theme.spacing(2),
        marginright: theme.spacing(2),
    },

    sideMarginsLarge: {
        marginLeft: theme.spacing(4),
        marginright: theme.spacing(4),
    },
});

export const FilterBarStyleClasses = (theme: Theme) => createStyles({
    appTabFilterInsideRoot: {
        color: theme.palette.common.white,
        backgroundColor: "transparent",
        boxShadow: "none"
    },

    appTabFilterRoot: {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.main,
        boxShadow: "none",
        padding: theme.spacing(1)
    },

    filterTabContainer: {
        marginBottom: theme.spacing(2),
    },
});

export const HightlightStyleClasses = (theme: Theme) => createStyles({
    newItemLeftMark: {
        borderLeftColor: orange[500],
        borderLeftStyle: "solid",
        borderLeftWidth: 4,
        paddingLeft: 4
    },
});

export const DrawerBoxStyleClasses = (theme: Theme) => createStyles({

    root: {
        zIndex: theme.zIndex.modal
    },

    paper: {
        width: RightDrawerWidth,
        padding: theme.spacing(1),
        margin: theme.spacing(1),
        backgroundColor: theme.palette.common.white,
    },

    container: {
        height: "100%",
        overflow: "hidden",
    },

    contentContainer: {
        flexGrow: 1,
        overflowY: "auto",
        overflowX: "hidden",
    },

    titleContainer: {
        backgroundColor: theme.palette.common.white,
        marginBottom: theme.spacing(),
        padding: theme.spacing(),
        paddingRight: 0
    },

    title: {
        textTransform: "uppercase",
        fontSize: "1rem"
    },

    titlePaper: {
        paddingRight: 0
    }
});

export const PaginationStyleClasses = (theme: Theme) => createStyles({
    textPrimary: {
        "&:hover": {
            color: theme.palette.common.black,
            backgroundColor: theme.palette.grey[400]
        }
    },
    textSecondary: {
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.common.white,
        "&:hover": {
            backgroundColor: theme.palette.secondary.dark
        }
    }
});

export type GlobalSearchClassKey =
    | "userCard"
    | "courseCard"
    | "topicCard"
    | "documentCard"
    | "publicationCard"
    ;

export const GlobalSearchStyleClasses = (theme: Theme) => createStyles({
    userCard: {
        borderLeft: "solid 10px navy"
    },
    courseCard: {
        borderLeft: "solid 10px darkgreen"
    },
    topicCard: {
        borderLeft: "solid 10px purple"
    },
    documentCard: {
        borderLeft: "solid 10px brown"
    },
    publicationCard: {
        borderLeft: "solid 10px orange"
    }
});

////////////////////////////////////////////////////////////////
// STYLES
////////////////////////////////////////////////////////////////

export const TabLargeStyle: React.CSSProperties = {
    fontSize: "1rem"
};

export const GrayBarStyle: React.CSSProperties = {
    background: grey[500],
};

export const TableAlignTopStyle: React.CSSProperties = {
    verticalAlign: "top"
};

////////////////////////////////////////////////////////////////
